import { ReactComponent as OutlineInfoCircle16 } from "assets/svg/outline-info-circle-16.svg";
import { useCalendarAppointments } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarAppointmentsContext";
import formatSessionStatus from "components/invoicing/utils/formatSessionStatus";
import messages from "messages";
import { useCallback, useMemo } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import createInvoice from "services/invoicing/createInvoice";
import waitForInvoice from "services/invoicing/wait-for-invoice";
import {
  addAppointmentWithInvoiceCreationInProgress,
  removeAppointmentWithInvoiceCreationInProgress,
  selectFocusedSessionAppointment,
  selectFocusedSessionNextStatus,
  setFocusedSession,
} from "store/calendarSidebarSlice";
import { store } from "store/store";
import formatPriceInUsd from "utils/formatPriceInUsd";
import sessionSidesheetStyles from "../../SessionSidesheet.module.scss";
import InvoiceRow from "./InvoiceRow";

import getClasses from "utils/getClasses";
import styles from "./PaymentSessionDetails.module.scss";
import { useTeamContext } from "contexts/group-practice-context/TeamContext";

interface PaymentSessionDetailsSectionProps {
  appointment: AppointmentWithSeriesInfo & {
    invoice?: InvoiceData;
  };
  isInThePast: boolean;
  isWaitingForInvoice: boolean;
  trackClick: (name: string) => void;
  isCancelled: boolean;
  invoiceSessionStatus: string;
  feeCharged: string | null;
  setError: React.Dispatch<any>;
}

const PaymentSessionDetailsSection = ({
  appointment,
  isInThePast,
  isWaitingForInvoice,
  trackClick,
  isCancelled,
  invoiceSessionStatus,
  feeCharged,
  setError,
}: PaymentSessionDetailsSectionProps) => {
  const dispatch = useDispatch();
  const nextStatus = useSelector(selectFocusedSessionNextStatus);

  const { forceRefreshAppointments } = useCalendarAppointments();

  const isPutHoldSessionComplete = useMemo(() => {
    if (appointment.statusFlags.usingInvoicing) {
      return false;
    }

    if (isCancelled) {
      return false;
    }

    if (!isInThePast) {
      return false;
    }

    if (appointment.statusFlags.wasNotPaid && appointment.pricing.list_price) {
      return false;
    }

    return true;
  }, [appointment, isInThePast, isCancelled]);

  const sessionStatusLabel = useMemo(() => {
    if (appointment.statusFlags.usingInvoicing && !isCancelled) {
      return ""; // For invoice session, don't show the status here unless session is cancelled
    }

    if (appointment.statusFlags.usingInvoicing) {
      return formatSessionStatus(
        nextStatus ? nextStatus : invoiceSessionStatus,
      );
    }

    if (appointment.cancel_refund_offer?.cancellation_price) {
      return "Late Cancel";
    }

    if (appointment.confirmed_attendance === "patient_no_show") {
      return "No Show";
    }

    if (appointment.statusFlags.cancelledByPatient) {
      return "Cancel by Patient";
    }

    if (appointment.statusFlags.cancelledByOrchidPro) {
      return "Cancel";
    }

    if (isCancelled) {
      return "Cancel";
    }

    if (isPutHoldSessionComplete) {
      return "Complete";
    }

    return "";
  }, [
    appointment,
    invoiceSessionStatus,
    isCancelled,
    nextStatus,
    isPutHoldSessionComplete,
  ]);

  const cancellationPolicyText = useMemo(() => {
    if (appointment.statusFlags.usingInvoicing) {
      return "";
    }

    if (appointment.cancellation_policy.cancellation_policy_flat_fee) {
      return `Flat Fee - $${appointment.cancellation_policy.cancellation_policy_flat_fee}`;
    }

    if (appointment.cancellation_policy.cancellation_policy_percent_fee) {
      return `${appointment.cancellation_policy.cancellation_policy_percent_fee}% of Session Fee`;
    }

    return "No cancellation policy";
  }, [appointment]);

  const paidAmount = useMemo(() => {
    if (appointment.statusFlags.usingInvoicing) {
      return `$${(appointment.invoice?.amount_total_paid || 0).toFixed(2)}`;
    }

    if (appointment.cancel_refund_offer) {
      return `$${appointment.cancel_refund_offer.cancellation_price?.toFixed(
        2,
      )}`;
    }

    return "";
  }, [appointment]);

  const totalChargedText = useMemo(() => {
    if (isCancelled) {
      return (
        [feeCharged, paidAmount].filter((part) => part?.length).join("-") || ""
      );
    }

    if (
      isPutHoldSessionComplete &&
      appointment.pricing.amount_paid_by_patient
    ) {
      return formatPriceInUsd(appointment.pricing.amount_paid_by_patient);
    }

    return "";
  }, [
    appointment,
    feeCharged,
    paidAmount,
    isCancelled,
    isPutHoldSessionComplete,
  ]);
  const { practiceOwnerOrchidUserId } = useTeamContext();

  const requestInvoice = useCallback(async () => {
    try {
      dispatch(
        addAppointmentWithInvoiceCreationInProgress(appointment.appointment_id),
      );
      await createInvoice(appointment.appointment_id, practiceOwnerOrchidUserId);
      // even if invoice data is not ready, still redirect to edit page (with loading status)
      window.open(
        `/pro-dashboard/invoicing/edit/appointment/${appointment.appointment_id}`,
        "_blank",
      );
      const invoice = await waitForInvoice(appointment.appointment_id);
      dispatch(
        removeAppointmentWithInvoiceCreationInProgress(
          appointment.appointment_id,
        ),
      );
      forceRefreshAppointments();
      if (
        selectFocusedSessionAppointment(store.getState())?.appointment_id !==
        appointment.appointment_id
      ) {
        return;
      }
      dispatch(setFocusedSession({ ...appointment, invoice }));
    } catch (error) {
      dispatch(
        removeAppointmentWithInvoiceCreationInProgress(
          appointment.appointment_id,
        ),
      );
      setError(error);
    }
  }, [
    appointment,
    dispatch,
    forceRefreshAppointments,
    setError,
    practiceOwnerOrchidUserId,
  ]);

  return (
    <div className={sessionSidesheetStyles["section"]}>
      <div className={sessionSidesheetStyles["section__title"]}>
        {messages.payment}
      </div>

      <div className={styles["row"]}>
        <div className={styles["label"]}>{messages.billingType}</div>
        <div className={styles["value"]}>{messages.selfPay}</div>
      </div>

      <div className={styles["row"]}>
        <div className={styles["label"]}>{messages.paymentMethod}</div>
        <div className={styles["value"]}>
          {appointment.statusFlags.usingInvoicing ? (
            messages.invoicedSession
          ) : (
            <div className={sessionSidesheetStyles["text-with-info-tooltip"]}>
              <OverlayTrigger
                placement="left"
                overlay={
                  <Tooltip id="preauthorized-cc-tooltip">
                    {messages.preauthorizedCcTooltipLine1}
                    <br />
                    {messages.preauthorizedCcTooltipLine2}
                  </Tooltip>
                }
              >
                <OutlineInfoCircle16 className={"blue ml-1"} />
              </OverlayTrigger>
              {messages.preauthorizedCc}
            </div>
          )}
        </div>
      </div>

      {sessionStatusLabel && (
        <div className={styles["row"]}>
          <div className={styles["label"]}>{messages.sessionStatus}</div>
          <div
            className={getClasses(
              styles["value"],
              isCancelled && styles["value--cancelled"],
              isPutHoldSessionComplete && styles["value--complete"],
            )}
          >
            {sessionStatusLabel}
          </div>
        </div>
      )}

      {isCancelled && cancellationPolicyText && (
        <div className={styles["row"]}>
          <div className={styles["label"]}>{messages.cancellationPolicy}</div>
          <div className={styles["value"]}>{cancellationPolicyText}</div>
        </div>
      )}

      {totalChargedText && (
        <div className={styles["row"]}>
          <div className={styles["label"]}>{messages.totalCharged}</div>
          <div className={styles["value"]}>{totalChargedText}</div>
        </div>
      )}

      <InvoiceRow
        appointment={appointment}
        nextStatus={nextStatus}
        isWaitingForInvoice={isWaitingForInvoice}
        trackClick={trackClick}
        requestInvoice={requestInvoice}
      />
    </div>
  );
};

export default PaymentSessionDetailsSection;
